import type { RunAggregateProjectionV2, WebTestRun } from '@blume/web-test-model'
import { ArrowRight } from 'lucide-react'
import { useMemo } from 'react'

import { relativeTime } from '@/design/format'
import { SuiteAvatarTile, scenarioEmoji } from '@/ui/atoms/avatar-tile'

import { independentSuiteKey, StatusDistribution, suiteHref, suiteSummaries } from './entity-shared'
import { EmptyState, GroupCard, MetaLine, PageTitle } from './parts'

export function SuitesPage({
  runs,
  aggregates,
  now
}: {
  runs: WebTestRun[]
  aggregates?: RunAggregateProjectionV2[]
  now: number
}) {
  const groups = useMemo(() => suiteSummaries(runs, aggregates), [aggregates, runs])

  return (
    <>
      <PageTitle
        title="Suites"
        subtitle="Health and coverage per suite run — where verification effort is going. Open a suite for its scenarios and run evidence."
      />
      {groups.length === 0 ? (
        <EmptyState>No suite runs yet.</EmptyState>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => {
            const progress = group.progress
            const terminal = progress.total - progress.counts.active
            const passRate =
              terminal === 0 ? undefined : Math.round((progress.counts.passed / terminal) * 100)
            // The ad-hoc bucket is run context, not a suite entity — it gets
            // no entity page and therefore no card-level link.
            const isSuiteEntity = group.key !== independentSuiteKey
            const observedEmojis = group.runs.map((run) => scenarioEmoji(run.scenarioId))
            return (
              <section
                key={group.key}
                aria-label={group.label}
                aria-disabled={isSuiteEntity ? undefined : true}
              >
                <GroupCard
                  className={isSuiteEntity ? undefined : 'bg-muted/45 opacity-80'}
                  {...(isSuiteEntity
                    ? { link: { href: suiteHref(group.key), label: `Open suite ${group.label}` } }
                    : {})}
                  header={
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <SuiteAvatarTile id={group.key} emojis={observedEmojis} size="regular" />
                      <h2 className="inline-flex items-center gap-1.5 font-mono text-base font-semibold">
                        {isSuiteEntity ? group.label : 'Ad-hoc runs — execution context'}
                        {isSuiteEntity ? (
                          <ArrowRight aria-hidden className="size-4 text-muted-foreground" />
                        ) : null}
                      </h2>
                      <MetaLine
                        items={[
                          `${progress.total} run${progress.total === 1 ? '' : 's'}`,
                          // The percentage states its own fraction: passed over
                          // finished (terminal) runs. In-flight runs are excluded
                          // from the denominator; cancelled and infra outcomes
                          // count as finished and are named in the legend below.
                          passRate === undefined
                            ? undefined
                            : `${passRate}% passed — ${progress.counts.passed} of ${terminal} finished run${terminal === 1 ? '' : 's'}`,
                          group.updatedAt
                            ? `latest ${relativeTime(group.updatedAt, now)}`
                            : undefined
                        ]}
                      />
                    </div>
                  }
                >
                  <StatusDistribution progress={progress} />
                  {!isSuiteEntity ? (
                    <p className="mt-3 text-[13px] text-muted-foreground">
                      Not a suite and not clickable. These runs were started outside suite
                      execution.
                    </p>
                  ) : null}
                </GroupCard>
              </section>
            )
          })}
        </div>
      )}
    </>
  )
}
